// ============================================================================
// Ask AI's My Work card — a row's own buttons (item #944, the My Work part;
// TCSD-0531 one tool per part, saved through the screen's own function). The
// server half is supabase/functions/am-copilot/my-work.ts.
//
// Every save here is the function the row's own button calls
// (src/modules/home/hand-off.ts, item #835): Send to… → sendTo, Clear →
// clearRow, Reopen → undoRow (the same doors the history page's Reopen uses).
// The row is rebuilt from the database's own row with fromDb, as Home does.
// ============================================================================

import { clearRow, sendTo, undoRow } from '../home/hand-off';
import { fromDb, type HomeWorkDbRow } from '../home/home-work';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { myWorkButtonWords } from '../../../supabase/functions/am-copilot/buttons';
import type { FieldSpec } from './building-actions';

export const MY_WORK_ACTIONS = ['send_to', 'clear', 'reopen'] as const;
export type MyWorkAction = (typeof MY_WORK_ACTIONS)[number];

/** A draft as the server persisted it (prepareMyWorkAction). */
export interface MyWorkDraft {
  item_type: string;
  item_id: string;
  action: MyWorkAction;
  fields: Record<string, unknown>;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface MyWorkCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  readBack: string | null;
  doItLabel: string | null;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const ctxOf = (d: MyWorkDraft) => (d.context ?? {}) as Record<string, unknown>;

/** The row as Home names it. */
export const myWorkOf = (d: MyWorkDraft) => str(ctxOf(d).name) || str(d.display_name) || d.item_id;

/** What the card shows for this draft, with the person's edits `f`. */
export function myWorkCard(d: MyWorkDraft, f: Record<string, unknown>): MyWorkCardSpec {
  const c = ctxOf(d);
  const asks = str(c.missing);
  const what = asks ? [{ label: 'Asking for', value: asks }] : [];
  const base: MyWorkCardSpec = { title: '', fields: [], info: what, readBack: null, doItLabel: myWorkButtonWords(d.action, c), missing: null };
  switch (d.action) {
    case 'send_to':
      return {
        ...base,
        title: `Send to ${str(c.to_name) || 'them'}`,
        info: [...what, { label: 'To', value: str(c.to_name) || str(f.to_eid) }],
        fields: [{ key: 'why', label: 'Why? (optional)', kind: 'textarea' }],
        readBack: `This hands it to ${str(c.to_name) || 'them'}, who is told, and takes it off your list. Undo brings back only your own copy.`,
      };
    case 'clear':
      return { ...base, title: 'Clear it from your list', readBack: 'This takes it off your list only — whoever else has it still has it.' };
    case 'reopen':
      return { ...base, title: 'Put it back on your list', info: [] };
  }
}

export interface MyWorkCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}
const fail = (error: string): MyWorkCommitResult => ({ ok: false, error });

/** Save one My Work draft through the row's own function. */
export async function commitMyWorkAction(d: MyWorkDraft, f: Record<string, unknown>, eid: string | null): Promise<MyWorkCommitResult> {
  if (!eid) return fail('Your sign-in has no employee record, so nothing can be saved in your name.');
  const c = ctxOf(d);
  const dbRow = (c.row ?? null) as HomeWorkDbRow | null;
  const name = myWorkOf(d);
  const isNotice = d.item_type === 'notification';
  try {
    switch (d.action) {
      case 'send_to': {
        if (!dbRow) return fail('That row could not be read — open My Work and send it from there.');
        const to = str(f.to_eid);
        await sendTo(fromDb(dbRow), to, str(f.why));
        return { ok: true, note: `Sent to ${str(c.to_name) || to} — ${name}.`, target_table: isNotice ? 'notification_recipients' : 'record_routing', target_row_id: d.item_id, committed_payload: { to_eid: to, why: str(f.why).trim() || null } };
      }
      case 'clear': {
        if (!dbRow) return fail('That row could not be read — open My Work and clear it from there.');
        await clearRow(fromDb(dbRow));
        return { ok: true, note: `Cleared from your list — ${name}.`, target_table: isNotice ? 'notification_recipients' : 'my_work_dismissals', target_row_id: d.item_id, committed_payload: { cleared: true } };
      }
      case 'reopen':
        await undoRow({ kind: isNotice ? 'notification' : 'derived', itemType: d.item_type, itemId: d.item_id });
        return { ok: true, note: `Back on your list — ${name}.`, target_table: isNotice ? 'notification_recipients' : 'my_work_dismissals', target_row_id: d.item_id, committed_payload: { reopened: true } };
    }
  } catch (e) {
    return fail(rpcErrorMessage(e, 'That did not save.'));
  }
}
